import React, { useRef, useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome/index.es.js";  // This import only works for FA v6.7.2 + react-fontawesome@0.1.18. 
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

/**
 * Redux-Form compatible dropdown that renders a custom menu (not a native <select>).
 * Expects props from <Field component={RichDropdown} ... />
 * options: [{ value, label }] where label MAY be JSX.
 */
export default function RichDropdown({ 
  input,
  meta = {},
  options = [],
  placeholder,
  disabled,
  className,
  onChange,
  readonly,
  placeholderAdornment,
  val,
  t
}) {
    const [open, setOpen] = useState(false);
    const [hoverIndex, setHoverIndex] = useState(null);   // “hover” highlight index
    const [navMode, setNavMode] = useState(null);         // 'mouse' | 'keys' | 'type'
    const typeBufRef = useRef("");                        // accumulated typed chars
    const typeTimerRef = useRef(null);                    // buffer reset timer

    const btnRef = useRef(null);
    const menuRef = useRef(null);
    const itemRefs = useRef([]);                          // refs to menu items
    const openedByTypingRef = useRef(false);              // remember if we opened due to typing
    const lastMouseIndexRef = useRef(null);               // remember last mouseover index
    const keyboardLockUntilRef = useRef(0);

    const isKeyboardLocked = () => Date.now() < keyboardLockUntilRef.current;
    const armKeyboardLock = (ms = 250) => { keyboardLockUntilRef.current = Date.now() + ms; };

    // Normalize Redux-Form value: if an event slipped through, unwrap it
    const rfValue = (input.value && input.value.target)
      ? input.value.target.value
      : input.value;

    // Prefer external 'val' when provided (mimics Dropdown.js behavior)
    const effectiveValue = (typeof val !== "undefined" ? val : rfValue);

    const valuesEqual = (a, b) => String(a) === String(b);

    // Keep Redux-Form in sync if caller forces an external value
    useEffect(() => {
      if (typeof val !== "undefined" && String(val) !== String(rfValue)) {
        input.onChange({ target: { name: input.name, value: val } });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [val]);

    // Find the matching option for display
    const selected = options.find(
      (opt) => String(opt.value) === String(effectiveValue)
    );

    useEffect(() => {
      const onDocClick = (e) => {
          if (!open) return;
          if (
              btnRef.current && !btnRef.current.contains(e.target) &&
              menuRef.current && !menuRef.current.contains(e.target)
          ) setOpen(false);
      };

      if (open && openedByTypingRef.current && menuRef.current) {
        // After opening via typing, move focus to the menu so
        // the next keys land here and keep building the buffer.
        menuRef.current.focus();
        openedByTypingRef.current = false;
      }

      document.addEventListener("mousedown", onDocClick);
      return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    const handleSelect = (opt) => {
      const value = (opt && opt.__isPlaceholder) ? "null" : (opt && opt.value);

      // current value shown by the field (prefer external `val` if your component supports it)
      const cur = (typeof val !== "undefined" ? val : rfValue);

      // If selecting the same thing, do nothing (no dirtying, no re-render churn)
      if (String(value) === String(cur)) {
        setOpen(false);
        return; // keep menu state unchanged (don’t close)
      }

      const evt = { target: { name: input.name, value } };
      // Single dispatch — exactly like native <select> → Dropdown.js
      input.onChange(evt);
      setOpen(false);
    };

    const renderLabel = (optOrString) => {
      const content = (optOrString && optOrString.label !== undefined)
        ? optOrString.label
        : optOrString;

      // If a custom JSX label is already provided (Trigger), keep it
      if (React.isValidElement(content)) return content;

      // Otherwise, wrap plain text so CSS can apply consistently
      return (
        <span className="nut-opt">
          <span className="nut-name">{String(content || "")}</span>
        </span>
      );
    };

    const displayOptions = placeholder
      ? [{
          value: "null",
          __isPlaceholder: true,
          label: (
            <span className="nut-opt">
              <span className="nut-name">{String(placeholder)}</span>
              {/* reuse the same adornment used in the closed field; fallback to icons */}
              {placeholderAdornment ? (
                <span className="nut-badges">
                  <i className="fa fa-check nut-check-ico" aria-hidden="true" />
                  <span className="nut-sep">|</span>
                  <i className="fa fa-times nut-x-ico" aria-hidden="true" />
                </span>
              ) : ""}
            </span>
          ),
        }, ...options]
      : options;

    // const displayOptions = placeholder
    //   ? [{
    //       value: "",
    //       __isPlaceholder: true,
    //       label: (
    //         <span className="nut-opt">
    //           <span className="nut-name">{String(placeholder)}</span>
    //           {/* Only show icons in the MENU if caller explicitly asks for it */}
    //           {menuPlaceholderAdornment || null}
    //         </span>
    //       ),
    //     }, ...options]
    //   : options;


    // Recursively flatten any ReactNode into plain text
    const extractText = (node) => {
      if (node == null) return "";
      if (typeof node === "string" || typeof node === "number") return String(node);
      if (Array.isArray(node)) return node.map(extractText).join(" ");
      // React element: dive into children
      if (React.isValidElement(node)) return extractText(node.props && node.props.children);
      return "";
    };

    // Get the text we should use for type-to-navigate
    const getOptionText = useCallback((opt) => {
      const raw = opt && opt.label;

      // Case 1: normal string label
      if (typeof raw === "string") return raw.toLowerCase();

      // Case 2: JSX label (Trigger dropdown etc.)
      if (React.isValidElement(raw)) {
        // First, see if there's a .nut-name span inside
        let found = "";
        const findNutName = (el) => {
          if (!React.isValidElement(el)) return;
          const cls = (el.props && el.props.className || "").split(/\s+/);
          if (cls.includes("nut-name")) {
            found = extractText(el.props && el.props.children);
          } else {
            React.Children.forEach(el.props && el.props.children, (c) => findNutName(c));
          }
        };
        findNutName(raw);

        // Prefer the .nut-name text; otherwise flatten everything
        const text = (found || extractText(raw)).replace(/\s+/g, " ").trim();
        return text.toLowerCase();
      }

      // Case 3: anything else
      return String(raw || "").toLowerCase();
    }, []);

    // Find first item whose text starts with 'needle' (skipping empty/placeholder values)
    const findPrefixIndex = useCallback((needle, arr) => {
      if (!needle) return -1;
      const n = needle.toLowerCase();
      for (let i = 0; i < arr.length; i++) {
        const o = arr[i];
        if (!o) continue;
        if (o.value === "null" || o.value === "empty") continue; // skip placeholders
        if (getOptionText(o).startsWith(n)) return i;
      }
      return -1;
    }, [getOptionText]);

    // Scroll the hover item into view
    const scrollHoverIntoView = useCallback((idx) => {
      const el = itemRefs.current[idx];
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ block: "nearest" });
      }
    }, []);

    // Handle typing (works both open & closed)
    const handleTypeChar = useCallback((ch, displayOptions) => {
      setNavMode("type");

      armKeyboardLock(); // prevent immediate mouse re-takeover while we jump

      // accumulate
      const prev = typeBufRef.current;
      const next = prev + ch.toLowerCase();
      typeBufRef.current = next;
      // reset timer
      if (typeTimerRef.current) clearTimeout(typeTimerRef.current);
      typeTimerRef.current = setTimeout(() => { typeBufRef.current = ""; }, 500);
      // find match
      const idx = findPrefixIndex(next, displayOptions);
      if (idx >= 0) {
        setHoverIndex(idx);
        // open if closed, then scroll
        if (!open) {
          openedByTypingRef.current = true;  // <-- remember why we opened
          setOpen(true);                     // auto-open on first typed char
        }
        // scroll after DOM paints
        requestAnimationFrame(() => scrollHoverIntoView(idx));
      }
    }, [findPrefixIndex, open, scrollHoverIntoView]);

    // Minimal keyboard nav (arrows/enter/escape + printable chars)
    const onKeyDown = useCallback((e, displayOptions) => {
      // Printable characters (letters/numbers/some punctuation)
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        handleTypeChar(e.key, displayOptions);
        return;
      }
      if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
        e.preventDefault();
        setOpen(true);
        setNavMode("keys");
        return;
      }
      if (!open) return;
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setNavMode("keys");
        armKeyboardLock();
        // If no current hoverIndex, seed from the last moused-over item
        const seed = (hoverIndex == null
          ? (lastMouseIndexRef.current != null ? lastMouseIndexRef.current : 0)
          : hoverIndex);
        const next = Math.min(seed + 1, displayOptions.length - 1);

        setHoverIndex(next);
        requestAnimationFrame(() => scrollHoverIntoView(next));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setNavMode("keys");
        armKeyboardLock();
        const seed = (hoverIndex == null
          ? (lastMouseIndexRef.current != null ? lastMouseIndexRef.current : 0)
          : hoverIndex);
        const prev = Math.max(seed - 1, 0);

        setHoverIndex(prev);
        requestAnimationFrame(() => scrollHoverIntoView(prev));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (hoverIndex != null && displayOptions[hoverIndex]) {
          // select the hovered item
          const opt = displayOptions[hoverIndex];
          // reuse your existing select logic
          handleSelect(opt);
        }
      }
    }, [open, hoverIndex, handleTypeChar, scrollHoverIntoView]);

    // Mouseover should move the highlight to the hovered item
    const onItemMouseEnter = (idx) => {
      if (isKeyboardLocked()) return;   // ignore transient mouse enters during keyboard nav

      lastMouseIndexRef.current = idx;  // remember where the mouse last was
      setNavMode("mouse");
      setHoverIndex(idx);
    };

    const indexFromEvent = (e) => {
      const rowEl = e.target && e.target.closest ? e.target.closest(".rich-dd-item") : null;
      if (!rowEl) return -1;
      return itemRefs.current ? itemRefs.current.indexOf(rowEl) : -1;
    };



    
    return (
        <div 
          className={`rich-dd ${className || ""} ${disabled ? "is-disabled" : ""} ${open ? "is-open" : ""}`}
        >

        {/* When navigating with keyboard, suppress native :hover background via a scoped style override */}
        {open && (
          <style>{`
            /* In keyboard mode, ignore mouse hover... unless it's the focused (.is-hover) item */
            .rich-dd-menu .rich-dd-item:hover:not(.is-hover) {
              background: transparent !important;
            }
          `}</style>
        )}
 
        <div className="select_box">  {/* match existing select wrapper from App.css */}
            <button
                type="button"
                ref={btnRef}
                className="rich-dd-btn"
                onClick={() => !disabled && setOpen(!open)}
                onKeyDown={(e) => onKeyDown(e, displayOptions)} // allow typing when closed
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className="rich-dd-value">
                  {selected
                    ? renderLabel(selected)
                    : (
                      <span className="nut-opt">
                        <span className="nut-name">{String(placeholder || (t && t("general.SELECT_A_USER")))}</span>
                        {/* show optional right-side adornment for placeholder */}
                        {/* {placeholderAdornment || null} */}
                      </span>
                    )
                  }
                </span>

                <FontAwesomeIcon
                    className="rich-dd-caret"
                    icon={faChevronDown}
                />

            </button>

            </div>
            {/* menu here */}
            {open && !disabled && (
                <div 
                  className="rich-dd-menu" 
                  ref={menuRef} 
                  role="listbox"
                  onKeyDown={(e) => onKeyDown(e, displayOptions)} // typing/nav while open
                  onMouseMove={(e) => {
                    if (isKeyboardLocked()) return;   // don't let tiny mouse wiggles hijack highlight
                    
                    // flip to mouse mode and sync highlight to the row actually under the cursor
                    const idx = indexFromEvent(e);
                    if (idx !== -1) {
                      if (navMode !== "mouse") setNavMode("mouse");
                      if (hoverIndex !== idx) setHoverIndex(idx);
                    }
                  }}
                >
                    {displayOptions.map((opt, idx) => (
                        <div
                            key={String(opt.value)}
                            role="option"
                            aria-selected={String(opt.value) === String(rfValue)}
                            className={`rich-dd-item 
                              ${!opt.value ? "placeholder" : ""} 
                              ${String(opt.value) === String(rfValue) ? "is-active" : ""}
                              ${hoverIndex === idx ? "is-hover" : ""}
                            `}
                            onClick={() => handleSelect(opt)}
                            onMouseEnter={() => onItemMouseEnter(idx)} 
                            ref={(el) => (itemRefs.current[idx] = el)}
                        >
                            {renderLabel(opt)}
                        </div>
                    ))}
                </div>
            )}

            {(meta && meta.touched) && (meta && meta.error) && <div className="rich-dd-error">{meta.error}</div>}
        </div>
    );
}



// WEBPACK FOOTER //
// ./src/components/RichDropdown.js